import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, History } from 'lucide-react';
import { v4 } from 'uuid';
import { useAlertContext } from '../contexts/Alert';
import { USER_URL } from '../api_base.js';
import { buildHeaders } from '../utils/apiHeaders.js';
import { checkMaintenance } from '../utils/checkMaintenance.js';

const PAGE_SIZE = 10;

const LoginActivity = () => {
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const firstItemDate = useRef(Date.now());
    const { setAlerts } = useAlertContext();
    const navigate = useNavigate();

    const pushAlert = (text) => setAlerts(prev => ([...prev, { id: v4(), text }]));

    const fetchPage = async (pageNumber, append) => {
        try {
            const params = new URLSearchParams({
                pageNumber: String(pageNumber),
                firstItemDate: String(firstItemDate.current),
            });

            const res = await fetch(USER_URL + "/login-activity?" + params.toString(), {
                method: "GET",
                credentials: "include",
                headers: buildHeaders(),
            });
            const data = await res.json();

            if (!res.ok || data.failed) {
                if (data.signout) navigate("/auth");
                pushAlert(data.message || "Oops! something went wrong while loading your login activity.");
                return;
            }

            const items = data.data || [];
            setEntries(prev => append ? [...prev, ...items] : items);
            setHasMore(items.length === PAGE_SIZE);
            checkMaintenance(data , navigate)
        } catch (err) {
            pushAlert("Oops! something went wrong while reaching the server.");
        }
    }

    useEffect(() => {
        (async () => {
            setLoading(true);
            await fetchPage(1, false);
            setLoading(false);
        })();
    }, []);

    const loadMore = async () => {
        setLoadingMore(true);
        const nextPage = page + 1;
        await fetchPage(nextPage, true);
        setPage(nextPage);
        setLoadingMore(false);
    }

    return (
        <div className="page">
            <div className="page_header">
                <button className="back" onClick={() => navigate("/")}><ChevronLeft /></button>
                <div className="page_title">
                    <h2>Login Activity</h2>
                    <p>Your recent sign-ins across devices.</p>
                </div>
            </div>

            <div className="page_body">
                {loading ? (
                    <div className="empty_state"><span className="loading"></span></div>
                ) : entries.length === 0 ? (
                    <div className="empty_state">
                        <History size="40" />
                        <h3>No login activity yet</h3>
                    </div>
                ) : (
                    <>
                        <div className="activity_list">
                            {entries.map(entry => (
                                <div className="activity_row" key={entry.loginId}>
                                    <span className={`activity_dot ${entry.loggedout ? "loggedout" : ""}`}></span>
                                    <div className="activity_meta">
                                        <h4>{entry.deviceInfo?.platform || "Unknown device"} · {entry.location?.city ? `${entry.location.city}, ${entry.location.country}` : "Unknown location"}</h4>
                                        <span>{new Date(entry.loggedInAt).toLocaleString()}{entry.loggedout ? " · Signed out" : ""}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {hasMore ? (
                            <div className="load_more_row">
                                <button className="pill_button ghost" disabled={loadingMore} onClick={loadMore}>
                                    {loadingMore ? <span className="loading"></span> : "Load more"}
                                </button>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    )
}

export default LoginActivity;
